import DOMPurify from 'dompurify'
import { useMemo, useState } from 'react'
import Slider from 'src/components/ui/Slider/Slider'

import FeaturedProductCard from './FeaturedProductCard'
import { useFeaturedProductsData } from './useFeaturedProductsData'
import type { FeaturedProductsCMSProps } from './types'

export default function FeaturedProducts({
  enableSession = true,
  title,
  editorialHtml,
  badgeColor,
  productIds,
  theme,
}: FeaturedProductsCMSProps) {
  const isDarkTheme = theme?.trim().toLowerCase() === 'dark'
  const [fadeState, setFadeState] = useState({
    left: false,
    right: true,
  })

  const { loading, products } = useFeaturedProductsData({
    productIds,
  })

  const sanitizedEditorialHtml = useMemo(() => {
    if (!editorialHtml?.trim()) {
      return ''
    }

    if (typeof window === 'undefined') {
      return ''
    }

    return DOMPurify.sanitize(editorialHtml, {
      ALLOWED_TAGS: [
        'a',
        'b',
        'br',
        'em',
        'i',
        'li',
        'ol',
        'p',
        'span',
        'strong',
        'u',
        'ul',
      ],
      ALLOWED_ATTR: ['href', 'rel'],
    })
  }, [editorialHtml])

  const badgeColorValue = badgeColor?.trim() || '#0050C8'
  const validProducts = products.slice(0, 6)

  if (!enableSession || loading || validProducts.length < 2) {
    return null
  }

  const collectionLabel = title?.trim() || 'Coleção'

  return (
    <section
      className={`overflow-hidden ${
        isDarkTheme
          ? 'bg-restructure-black text-restructure-neutral-2'
          : 'bg-restructure-white text-restructure-neutral-13'
      }`}
      data-testid="featured-products"
    >
      <div className="mx-auto w-full max-w-[1440px] pl-4 py-8 md:px-0 md:py-14">
        <div className="flex flex-col gap-8 md:flex-row md:items-start md:gap-48">
          <header className="w-full md:max-w-80 md:flex-none md:pt-24">
            {editorialHtml?.trim() ? (
              <div
                suppressHydrationWarning
                className={`[word-break:break-word] text-balance flex flex-col justify-center text-heading-xs font-normal not-italic tracking-normal [&>p]:m-0 [&>p]:leading-normal [&>p]:whitespace-pre-line ${
                  isDarkTheme
                    ? 'text-restructure-neutral-2'
                    : 'text-restructure-neutral-13'
                }`}
                dangerouslySetInnerHTML={{ __html: sanitizedEditorialHtml }}
              />
            ) : null}

            <div className="mt-6 flex items-center gap-4">
              <span
                className="h-1 w-12"
                style={{ backgroundColor: badgeColorValue }}
                aria-hidden="true"
              />

              <span
                className={`text-body-sm font-bold uppercase tracking-normal ${
                  isDarkTheme
                    ? 'text-restructure-neutral-2'
                    : 'text-restructure-neutral-13'
                }`}
              >
                {collectionLabel}
              </span>
            </div>
          </header>

          <div className="min-w-0 w-full md:flex-1">
            <div className="relative">
              <div
                className={`featured-products-slider min-w-0 ${
                  isDarkTheme ? 'featured-products-slider--dark' : ''
                }`}
              >
                <Slider
                  itemQuantity={validProducts.length}
                  controlsStyle="middle"
                  renderOnlyActiveControls
                  disableInactiveControls
                  onNavigationStateChange={({ prev, next }) => {
                    setFadeState({
                      left: prev,
                      right: next,
                    })
                  }}
                  customStepWidth={{
                    desktop: 264,
                    tablet: 232,
                    mobile: 216,
                  }}
                  customStepQtty={{
                    desktop: 3,
                    tablet: 2,
                    mobile: 1,
                  }}
                >
                  {validProducts.map((product) => (
                    <li
                      key={product.productId}
                      className="size-52 min-w-52 snap-start md:size-60 md:min-w-60"
                    >
                      <FeaturedProductCard product={product} />
                    </li>
                  ))}
                </Slider>

                {fadeState.left && (
                  <div
                    aria-hidden="true"
                    className="featured-products-slider-fade featured-products-slider-fade--left"
                    data-testid="featured-products-slider-fade-left"
                  />
                )}

                {fadeState.right && (
                  <div
                    aria-hidden="true"
                    className="featured-products-slider-fade featured-products-slider-fade--right"
                    data-testid="featured-products-slider-fade-right"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
